import React, { useRef, useEffect, useCallback } from 'react';
import './useScrollableSync.css';

export const useScrollableSync = () => {
    const scrollableSections = useRef<(HTMLDivElement | null)[]>([]);

    const handleWheel = useCallback((event: WheelEvent) => {
        // Only handle if horizontal scroll is significant and larger than vertical
        if (Math.abs(event.deltaX) > 0.5 && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
            event.preventDefault();
            scrollableSections.current.forEach(section => section?.scrollTo?.({ left: section.scrollLeft + event.deltaX }));
        }
    }, []);

    // Custom smooth scroll animation
    const animateScroll = (element: HTMLDivElement, targetLeft: number, duration:number) => {
        const el = element;
        const startLeft = el.scrollLeft;
        const change = targetLeft - startLeft;
        const startTime = performance.now();

        function animate(currentTime: number) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const ease = 1 - ((1 - progress) ** 3);
            el.scrollLeft = startLeft + (change * ease);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        }
        requestAnimationFrame(animate);
    };

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            const delta = event.key === 'ArrowLeft' ? -120 : 120;
            scrollableSections.current.forEach(section => {
                if (section) {
                    animateScroll(section, section.scrollLeft + delta, 700);
                }
            });
        }
    }, []);

    // Cleanup event listeners on unmount to prevent memory leaks
    useEffect(() => () => {
        scrollableSections.current.forEach(section => {
            if (section) {
                section.removeEventListener('wheel', handleWheel);
                section.removeEventListener('keydown', handleKeyDown);
            }
        });
    }, [handleWheel, handleKeyDown]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const { scrollLeft } = event.target as HTMLDivElement;
        scrollableSections.current.forEach(section => section?.scrollTo?.({ left: scrollLeft }));
    };

    const handleScrollableRef = (index: number) => (el: HTMLDivElement | null) => {
        // Remove listeners from previous element if ref changes
        const prevEl = scrollableSections.current[index];
        if (prevEl && prevEl !== el) {
            prevEl.removeEventListener('wheel', handleWheel);
            prevEl.removeEventListener('keydown', handleKeyDown);
        }
        scrollableSections.current[index] = el;
        if (el) {
            el.addEventListener('wheel', handleWheel, { passive: false });
            el.addEventListener('keydown', handleKeyDown);
            // Ensure the element is focusable
            if (!el.hasAttribute('tabindex')) {
                el.setAttribute('tabindex', '0');
            }
            // Remove focus outline using a CSS class
            el.classList.add('ns-no-outline');
        }
    };

    const scrollToMonth = (monthIndex: number | undefined) => {
        if (monthIndex !== undefined && monthIndex > 0) {
            const scrollAmount = monthIndex * 130;
            scrollableSections.current.forEach(section => {
                if (section) {
                    section.scrollTo(scrollAmount, 0);
                }
            });
        }
    };

    return {
        handleScroll,
        handleScrollableRef,
        scrollToMonth,
    };
};
